// useWebRTCPeer — 管理 WebRTCPeer 生命周期
// 提供 sendFiles / acceptFiles / decline 等高层 API

import { useCallback, useRef, useState } from 'react';
import type { FileDto, TransferStatus } from '@kuaichuan/shared';
import { WebRTCPeer } from '../lib/webrtc/peer';
import type { WebRTCPeerHandlers } from '../lib/webrtc/peer';
import type { SignalingClient } from '../lib/signaling/client';
import { getStunServers } from './useSignaling';
import { useSessionStore } from '../store/sessionStore';
import { useToastStore } from '../components/common/Toast';

export interface UseWebRTCPeerResult {
  peer: WebRTCPeer | null;
  // 发送方：建立连接（创建 offer）
  sendTo: (signaling: SignalingClient, targetPeerId: string, targetAlias: string, files: File[]) => Promise<void>;
  // 接收方：接受 OFFER 后调用
  receiveFrom: (signaling: SignalingClient, peer: { id: string; alias: string }, sessionId: string, sdp: string) => Promise<void>;
  // 发送方：收到 ANSWER 后调用
  receiveAnswer: (sdp: string) => void;
  // 接收方：用户决定接受哪些文件
  acceptSelected: (selectedIds: string[]) => void;
  declineIncoming: () => void;
  cancelTransfer: () => void;
  // 当前等待用户确认的文件清单（接收方）
  pendingIncomingFiles: FileDto[] | null;
  setPendingIncomingFiles: (files: FileDto[] | null) => void;
}

export function useWebRTCPeer(): UseWebRTCPeerResult {
  const peerRef = useRef<WebRTCPeer | null>(null);
  const [peer, setPeer] = useState<WebRTCPeer | null>(null);
  const [pendingIncomingFiles, setPendingIncomingFiles] = useState<FileDto[] | null>(null);

  const sessionStore = useSessionStore();
  const toast = useToastStore((s) => s.show);

  const buildHandlers = (role: 'sender' | 'receiver'): WebRTCPeerHandlers => ({
    onStateChange: (state: TransferStatus) => {
      sessionStore.setStatus(state);
    },
    onFileList: (files: FileDto[]) => {
      // 接收方收到文件清单
      setPendingIncomingFiles(files);
      sessionStore.setFiles(files);
    },
    onFileProgress: (fileId: string, receivedBytes: number, total: number) => {
      // 接收方: receivedBytes < total；发送方: receivedBytes = 已发送字节
      sessionStore.updateFileProgress(fileId, receivedBytes);
      void total;
    },
    onFileReceived: (_fileId: string, blob: Blob, fileName: string) => {
      // 接收方：保存文件
      saveBlob(blob, fileName);
      sessionStore.setFileStatus(_fileId, 'done');
      toast(`已接收: ${fileName}`, 'success');
    },
    onFileAck: (fileId: string, success: boolean, error?: string) => {
      if (success) {
        sessionStore.setFileStatus(fileId, 'done');
      } else {
        sessionStore.setFileStatus(fileId, 'error', error);
      }
    },
    onDecline: () => {
      toast('对方拒绝了传输', 'warning');
    },
    onAllDone: () => {
      toast(role === 'sender' ? '全部发送完成' : '全部接收完成', 'success');
    },
    onCancel: (reason?: string) => {
      toast(`传输已取消${reason ? `: ${reason}` : ''}`, 'info');
    },
    onError: (err: Error) => {
      toast(err.message, 'error');
    },
  });

  const sendTo = useCallback(
    async (signaling: SignalingClient, targetPeerId: string, targetAlias: string, files: File[]) => {
      const stunServers = await getStunServers();
      const fileDtos: FileDto[] = files.map((f) => ({
        id: crypto.randomUUID(),
        fileName: f.name,
        size: f.size,
        fileType: classifyFile(f),
        mimeType: f.type,
      }));

      sessionStore.reset();
      sessionStore.setRole('sender');
      sessionStore.setRemote(targetPeerId, targetAlias);
      sessionStore.setFiles(fileDtos);

      const p = new WebRTCPeer({
        signaling,
        targetPeerId,
        isInitiator: true,
        stunServers,
        handlers: {
          ...buildHandlers('sender'),
          onDataChannelOpen: () => {
            // 数据通道就绪后立即发送文件清单（替代脆弱的 setTimeout）
            peerRef.current?.sendFileList();
          },
        },
      });
      // 注入 File 对象
      for (let i = 0; i < files.length; i++) {
        p.setFileBlob(fileDtos[i].id, files[i]);
      }
      p.setPendingFiles(fileDtos);
      peerRef.current = p;
      setPeer(p);
      await p.init();
    },
    [sessionStore],
  );

  const receiveFrom = useCallback(
    async (signaling: SignalingClient, remote: { id: string; alias: string }, sessionId: string, sdp: string) => {
      const stunServers = await getStunServers();
      sessionStore.reset();
      sessionStore.setRole('receiver');
      sessionStore.setRemote(remote.id, remote.alias);

      const p = new WebRTCPeer({
        signaling,
        targetPeerId: remote.id,
        isInitiator: false,
        stunServers,
        handlers: buildHandlers('receiver'),
      });
      peerRef.current = p;
      setPeer(p);
      await p.handleOffer(sessionId, sdp);
    },
    [sessionStore],
  );

  const acceptSelected = useCallback(
    (selectedIds: string[]) => {
      if (!peerRef.current) return;
      // 注册即将接收的文件
      const selected = (pendingIncomingFiles ?? []).filter((f) => selectedIds.includes(f.id));
      peerRef.current.registerIncomingFiles(selected);
      peerRef.current.acceptFiles(selectedIds);
      setPendingIncomingFiles(null);
    },
    [pendingIncomingFiles],
  );

  const declineIncoming = useCallback(() => {
    peerRef.current?.decline();
    setPendingIncomingFiles(null);
  }, []);

  const cancelTransfer = useCallback(() => {
    peerRef.current?.cancel();
  }, []);

  const receiveAnswer = useCallback((sdp: string) => {
    peerRef.current?.handleAnswer(sdp);
  }, []);

  return {
    peer,
    sendTo,
    receiveFrom,
    receiveAnswer,
    acceptSelected,
    declineIncoming,
    cancelTransfer,
    pendingIncomingFiles,
    setPendingIncomingFiles,
  };
}

// ===== 工具函数 =====

function classifyFile(file: File): FileDto['fileType'] {
  const t = file.type;
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t.startsWith('text/')) return 'text';
  return 'application';
}

function saveBlob(blob: Blob, fileName: string) {
  // 优先用 File System Access API（Chrome/Edge）
  const picker = (window as unknown as {
    showSaveFilePicker?: (opts: { suggestedName?: string }) => Promise<FileSystemFileHandle>;
  }).showSaveFilePicker;
  if (picker) {
    picker({ suggestedName: fileName })
      .then(async (handle) => {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      })
      .catch((err: { name?: string }) => {
        if (err?.name === 'AbortError') return; // 用户取消
        console.error('save error', err);
        fallbackDownload(blob, fileName);
      });
  } else {
    fallbackDownload(blob, fileName);
  }
}

function fallbackDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
